import { page as pageQuery, services as servicesQuery } from '@data/queries';
import { Container, Grid, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import sanity from '@utils/sanity';
import { ReactElement } from 'react';
import { PageProps } from 'src/@types/page';
import { ServiceProps } from 'src/@types/service';
import Image from 'src/components/NextSanityImage';
import Routes from 'src/routes';
import MissingService from 'src/sections/services/MissingService';
import ServicesList from 'src/sections/services/ServicesList';
import { Page } from '../../src/components';
import { HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT } from '../../src/config';
import Layout from '../../src/layouts';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: HEADER_MOBILE_HEIGHT,
  [theme.breakpoints.up('md')]: {
    paddingTop: HEADER_DESKTOP_HEIGHT,
  },
}));

const HeroStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(5, 0, 2, 0),
  backgroundColor: theme.palette.grey[100],
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(10, 0),
  },
}));

// ----------------------------------------------------------------------

type Props = {
  services: ServiceProps[];
  page: PageProps;
};

export default function ServicesPage({ services, page }: Props) {
  return (
    <Page seo={page.seo}>
      <RootStyle>
        <HeroStyle>
          <Container>
            <Grid container spacing={{ xs: 8, md: 3 }} justifyContent="space-between">
              <Grid
                item
                xs={12}
                md={6}
                lg={5}
                sx={{
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Typography variant="h2" component="h1">
                  Elektrikertjenester
                </Typography>
                <Typography sx={{ mt: 3, mb: 6 }}>
                  Vi har jobbet for lønnsomme og fremtidsrettede løsninger innen elektro i over 20
                  år. Det er ikke tilfeldig at vi i dag er blant de fremste elektroinstallatørene i
                  Bergen.
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Paper
                  sx={{
                    overflow: 'hidden',
                    width: 1,
                    height: 1,
                    position: 'relative',
                  }}
                >
                  <Image
                    alt="about"
                    src="image-b3cce5530454ed3155bbbf695d58fc4fb061b1d2-3072x1005-jpg"
                    sx={{ width: 1, height: 1 }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </HeroStyle>
        <Container>
          <ServicesList services={services} />
        </Container>

        <MissingService />
      </RootStyle>
    </Page>
  );
}

// ----------------------------------------------------------------------

ServicesPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

// ----------------------------------------------------------------------

export const getStaticProps = async () => {
  const services = await sanity.fetch(servicesQuery);
  const page = await sanity.fetch(pageQuery, { slug: Routes.services });

  return {
    props: { services, page }, // will be passed to the page component as props
  };
};
